// src/Dashboard/admin/sections/AnnouncementsSection.jsx
import { useMemo, useState } from "react";
import { Megaphone, Trash2, Search, X, Users, Target, Building2 } from "lucide-react";

/**
 * ✅ Updated behavior (as requested)
 * - REMOVE scope selection from "Create Announcement" UI.
 * - Scope (level + targetId) comes ONLY from the global Scope Selector (AdminDashboard).
 * - "Create Announcement" shows the current scope as read-only + Audience Picker only.
 *
 * Contract:
 * - onCreateAnnouncement(payload)
 *   payload = {
 *     level, targetId, title, body,
 *     audienceType: "scope"|"group"|"users",
 *     audienceKey?, audienceJson?, userIds?
 *   }
 *
 * Optional (for individuals search):
 * - searchAudienceUsers({ q, role, status }) -> Promise<array of users>
 *   user shape expected: { id, full_name, email, phone, role, is_active, account_status }
 *
 * Optional (for graduates_by_course group):
 * - courses = [{ slug, title }] OR [{ id, slug, name }]
 */

export default function AnnouncementsSection({
  /* ------------------------------------------------------------------ */
  /* Scope (from AdminDashboard global selector)                         */
  /* ------------------------------------------------------------------ */
  level = "union", // union|conference|station|district|church
  targetId = "", // "" for union or unspecified
  scopeLabel = "", // e.g. "Union (All)" or "District: Nairobi East"
  scopeChips = [], // optional: ["Conference: X", "Station: Y", ...]

  /* ------------------------------------------------------------------ */
  /* Create form (controlled from parent)                                */
  /* ------------------------------------------------------------------ */
  annTitle = "",
  setAnnTitle = () => {}, // ✅ prevents "o is not a function" crash
  annBody = "",
  setAnnBody = () => {}, // ✅ prevents "d is not a function" crash
  annLoading = false,
  onCreateAnnouncement = async () => {},

  /* ------------------------------------------------------------------ */
  /* List                                                               */
  /* ------------------------------------------------------------------ */
  annList = [],
  loadAnnouncements = null,
  onDeleteAnnouncement = async () => {},

  /* ------------------------------------------------------------------ */
  /* Optional                                                           */
  /* ------------------------------------------------------------------ */
  searchAudienceUsers, // async ({q, role, status}) => users[]
  courses = [], // optional: used for graduates_by_course
}) {
  /* ------------------------------------------------------------------ */
  /* Audience state (local to this section)                              */
  /* ------------------------------------------------------------------ */
  const [audienceType, setAudienceType] = useState("scope"); // scope | group | users
  const [submitErr, setSubmitErr] = useState("");

  // group
  const [audienceKey, setAudienceKey] = useState("all_learners");
  const [courseSlug, setCourseSlug] = useState(""); // for graduates_by_course

  // individuals
  const [searchQ, setSearchQ] = useState("");
  const [searchRole, setSearchRole] = useState("learner"); // learner|instructor|admin|all
  const [searchStatus, setSearchStatus] = useState("all"); // active|inactive|all
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [results, setResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // [{id, full_name, email, role}]

  const courseOptions = useMemo(() => {
    const list = Array.isArray(courses) ? courses : [];
    return list
      .map((c) => {
        const slug = String(c.slug || c.course_slug || c.id || "").trim();
        const label = c.title || c.name || slug || "—";
        return slug ? { slug, label } : null;
      })
      .filter(Boolean);
  }, [courses]);

  const safeScopeLabel =
    scopeLabel ||
    (String(level || "union").toLowerCase() === "union"
      ? "Union (All)"
      : `${level} (${targetId || "unspecified"})`);

  function resetAudience() {
    setAudienceType("scope");
    setAudienceKey("all_learners");
    setCourseSlug("");
    setSearchQ("");
    setSearchRole("learner");
    setSearchStatus("all");
    setSearchErr("");
    setResults([]);
    setSelectedUsers([]);
  }

  function prettyAudience(a) {
    const t = String(a?.audience_type || "scope").toLowerCase();
    if (t === "scope") return "Scope";

    if (t === "group") {
      const k = String(a?.audience_key || "").toLowerCase();
      if (k === "all_learners") return "Group: All learners";
      if (k === "active_learners") return "Group: Active learners";
      if (k === "inactive_learners") return "Group: Inactive learners";
      if (k === "instructors") return "Group: Instructors";
      if (k === "admins") return "Group: Admins";
      if (k === "graduates_by_course") {
        const slug =
          a?.audience_json?.courseSlug || a?.audience_json?.course_slug || "";
        return `Group: Graduates (${slug || "course"})`;
      }
      return `Group: ${k || "—"}`;
    }

    if (t === "users") {
      const count = Array.isArray(a?.audience_user_ids)
        ? a.audience_user_ids.length
        : 0;
      return `Individuals (${count})`;
    }

    return "Scope";
  }

  function userLabel(u) {
    return (
      u?.full_name ||
      u?.fullName ||
      u?.email ||
      u?.phone ||
      String(u?.id || "—")
    );
  }

  function userStatus(u) {
    if (typeof u?.is_active === "boolean") return u.is_active ? "active" : "inactive";
    const s = String(u?.account_status || "").toLowerCase();
    if (s === "inactive") return "inactive";
    return "active";
  }

  function addUser(u) {
    const id = String(u?.id || "").trim();
    if (!id) return;
    setSelectedUsers((prev) => {
      if (prev.some((x) => String(x.id) === id)) return prev;
      return [
        ...prev,
        {
          id,
          full_name: u?.full_name || u?.fullName || "",
          email: u?.email || "",
          role: u?.role || "",
        },
      ];
    });
  }

  function removeUser(id) {
    setSelectedUsers((prev) => prev.filter((u) => String(u.id) !== String(id)));
  }

  async function runSearch(e) {
    e?.preventDefault?.();
    setSearchErr("");

    if (!searchAudienceUsers) {
      setSearchErr("Audience search is not wired yet (missing searchAudienceUsers prop).");
      return;
    }

    const q = String(searchQ || "").trim();
    if (!q) {
      setSearchErr("Type something to search.");
      return;
    }

    try {
      setSearching(true);
      const out = await searchAudienceUsers({
        q,
        role: searchRole,
        status: searchStatus,
      });
      setResults(Array.isArray(out) ? out : []);
    } catch (err) {
      console.error(err);
      setSearchErr(err?.message || "Failed to search users.");
    } finally {
      setSearching(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitErr("");

    const resolvedLevel = String(level || "union").toLowerCase();
    const resolvedTargetId =
      resolvedLevel === "union"
        ? null
        : String(targetId || "").trim()
        ? String(targetId).trim()
        : null;

    // ✅ Block publish if scope is incomplete (prevents silent backend errors)
    if (resolvedLevel !== "union" && !resolvedTargetId) {
      setSubmitErr("Please select a valid target in the global scope selector (targetId missing).");
      return;
    }

    const payload = {
      level: resolvedLevel,
      targetId: resolvedTargetId,
      title: String(annTitle || "").trim(),
      body: String(annBody || "").trim(),
      audienceType,
    };

    if (!payload.title) {
      setSubmitErr("Title is required.");
      return;
    }
    if (!payload.body) {
      setSubmitErr("Message is required.");
      return;
    }

    if (audienceType === "group") {
      payload.audienceKey = audienceKey;
      if (audienceKey === "graduates_by_course") {
        const slug = String(courseSlug || "").trim();
        if (!slug) {
          setSubmitErr("Pick a course slug for Graduates (by course).");
          return;
        }
        payload.audienceJson = { courseSlug: slug };
      }
    }

    if (audienceType === "users") {
      if (!selectedUsers.length) {
        setSubmitErr("Pick at least one user for Individuals targeting.");
        return;
      }
      payload.userIds = selectedUsers.map((u) => String(u.id));
    }

    try {
      const res = await onCreateAnnouncement(payload);

      // ✅ If your handler returns { ok:false, error:"..." } show it nicely
      if (res && typeof res === "object" && res.ok === false) {
        setSubmitErr(res.error || "Failed to publish announcement.");
        return;
      }

      // ✅ clear form after publish (safe)
      setAnnTitle?.("");
      setAnnBody?.("");
      // keep audience selection as-is
    } catch (err) {
      console.error("[announcements.publish] UI error:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to publish announcement.";
      setSubmitErr(msg);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Reorder list (newest first)                                         */
  /* ------------------------------------------------------------------ */
  const orderedAnnouncements = useMemo(() => {
    const list = Array.isArray(annList) ? annList : [];
    return [...list].sort((a, b) => {
      const da = toTime(a?.publish_at) ?? toTime(a?.created_at) ?? 0;
      const db = toTime(b?.publish_at) ?? toTime(b?.created_at) ?? 0;
      return db - da;
    });
  }, [annList]);

  /* ------------------------------------------------------------------ */
  /* Audience UI                                                        */
  /* ------------------------------------------------------------------ */
  const audiencePanel = (
    <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-4 bg-white dark:bg-slate-900 min-w-0">
      <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
        <div className="flex items-start gap-2 min-w-0">
          <Target size={16} className="text-orange-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 truncate">
              Audience
            </div>
            <div className="text-xs text-slate-500 truncate">
              Scope is already selected above. Audience refines who sees it (groups/individuals).
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={resetAudience}
          className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 inline-flex items-center gap-1 shrink-0"
          title="Reset audience settings"
        >
          <X size={14} /> Reset
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="min-w-0">
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
            Audience Type
          </label>
          <select
            value={audienceType}
            onChange={(e) => setAudienceType(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
          >
            <option value="scope">Scope (everyone in target)</option>
            <option value="group">Group (learners/admins/etc.)</option>
            <option value="users">Individuals (search & pick)</option>
          </select>
        </div>

        {audienceType === "group" && (
          <div className="md:col-span-2 min-w-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Group
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
              <select
                value={audienceKey}
                onChange={(e) => {
                  setAudienceKey(e.target.value);
                  setCourseSlug("");
                }}
                className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
              >
                <option value="all_learners">All Learners</option>
                <option value="active_learners">Active Learners</option>
                <option value="inactive_learners">Inactive Learners</option>
                <option value="graduates_by_course">Graduates (by course)</option>
                <option value="instructors">Instructors</option>
                <option value="admins">Admins</option>
              </select>

              {audienceKey === "graduates_by_course" && (
                <>
                  {courseOptions.length > 0 ? (
                    <select
                      value={courseSlug}
                      onChange={(e) => setCourseSlug(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
                    >
                      <option value="">Select Course…</option>
                      {courseOptions.map((c) => (
                        <option key={c.slug} value={c.slug}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={courseSlug}
                      onChange={(e) => setCourseSlug(e.target.value)}
                      placeholder="Course slug (e.g. discover)"
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
                    />
                  )}
                </>
              )}
            </div>

            {audienceKey === "graduates_by_course" && !courseSlug.trim() && (
              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Pick a course slug for graduates targeting.
              </div>
            )}
          </div>
        )}

        {audienceType === "users" && (
          <div className="md:col-span-3 min-w-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Find individuals
            </label>

            <form onSubmit={runSearch} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              <div className="lg:col-span-2 min-w-0">
                <div className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 min-w-0">
                  <Search size={16} className="text-slate-400 shrink-0" />
                  <input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    className="w-full bg-transparent outline-none text-sm min-w-0"
                    placeholder="Search name, email, phone…"
                  />
                </div>
              </div>

              <select
                value={searchRole}
                onChange={(e) => setSearchRole(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
              >
                <option value="learner">Learners</option>
                <option value="instructor">Instructors</option>
                <option value="admin">Admins</option>
                <option value="all">All roles</option>
              </select>

              <select
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                type="submit"
                disabled={searching}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-semibold disabled:opacity-60"
              >
                {searching ? "Searching…" : "Search"}
              </button>
            </form>

            {searchErr && <div className="mt-2 text-xs text-red-600">{searchErr}</div>}

            {selectedUsers.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2">
                  <Users size={14} /> Selected ({selectedUsers.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs max-w-full"
                    >
                      <span className="truncate max-w-[240px]">{u.full_name || u.email || u.id}</span>
                      <button
                        type="button"
                        onClick={() => removeUser(u.id)}
                        className="text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 shrink-0"
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 text-xs text-slate-600 dark:text-slate-300">
                Results ({results.length})
              </div>

              {results.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500">Search to find users to target.</div>
              ) : (
                <ul className="max-h-56 overflow-y-auto overflow-x-hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {results.map((u) => {
                    const id = String(u?.id || "");
                    const already = selectedUsers.some((x) => String(x.id) === id);

                    return (
                      <li
                        key={id}
                        className="px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 truncate">
                            {userLabel(u)}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {u?.email || "—"} • {String(u?.role || "—")} • {userStatus(u)}
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={already}
                          onClick={() => addUser(u)}
                          className={`w-full sm:w-auto px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                            already
                              ? "bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700 cursor-not-allowed"
                              : "bg-orange-600 text-white border-orange-600 hover:bg-orange-700"
                          }`}
                        >
                          {already ? "Added" : "Add"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {selectedUsers.length === 0 && (
              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Pick at least one user for Individuals targeting.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /* render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
      {/* Create */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
        <div className="flex items-start gap-3 mb-4 min-w-0">
          <div className="p-2 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg shrink-0">
            <Megaphone className="text-orange-600" size={22} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold truncate">Create Announcement</h3>
            <p className="text-xs text-slate-500">
              Scope is selected from the global scope selector. Only pick an audience here.
            </p>
          </div>
        </div>

        {/* ✅ Read-only scope summary (replaces Level/Target UI) */}
        <div className="mb-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-4">
          <div className="flex items-start gap-2">
            <Building2 size={16} className="text-orange-600 mt-0.5" />
            <div className="min-w-0">
              <div className="text-xs text-slate-500">Current scope</div>
              <div className="text-sm font-semibold truncate">{safeScopeLabel}</div>
              {Array.isArray(scopeChips) && scopeChips.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {scopeChips.slice(0, 6).map((c, i) => (
                    <span
                      key={`${c}-${i}`}
                      className="text-[11px] px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 min-w-0">
          {/* ✅ Audience before content */}
          {audiencePanel}

          <div className="min-w-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Title
            </label>
            <input
              value={annTitle}
              onChange={(e) => setAnnTitle(e.target.value)}
              required
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-100 dark:focus:ring-orange-500/30 bg-white dark:bg-slate-950 text-sm"
              placeholder="Announcement title…"
            />
          </div>

          <div className="min-w-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Message
            </label>
            <textarea
              value={annBody}
              onChange={(e) => setAnnBody(e.target.value)}
              required
              rows={4}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-100 dark:focus:ring-orange-500/30 bg-white dark:bg-slate-950 text-sm"
              placeholder="Write a brief announcement…"
            />
          </div>

          {/* ✅ Show publish errors instead of failing silently */}
          {submitErr ? (
            <div className="text-sm text-red-700 dark:text-red-200 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 rounded-xl p-3">
              {submitErr}
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={
                annLoading ||
                !String(annTitle || "").trim() ||
                !String(annBody || "").trim() ||
                (String(level || "union").toLowerCase() !== "union" &&
                  !String(targetId || "").trim()) ||
                (audienceType === "group" &&
                  audienceKey === "graduates_by_course" &&
                  !courseSlug.trim()) ||
                (audienceType === "users" && selectedUsers.length === 0)
              }
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white text-sm font-semibold hover:from-orange-700 hover:to-amber-700 disabled:opacity-60"
            >
              {annLoading ? "Publishing…" : "Publish"}
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
        <div className="flex items-start justify-between mb-4 gap-3 min-w-0">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold truncate">Announcements (current scope)</h3>
            <p className="text-xs text-slate-500 truncate">{safeScopeLabel}</p>
          </div>

          {loadAnnouncements ? (
            <button
              onClick={loadAnnouncements}
              className="text-xs text-orange-700 dark:text-orange-300 hover:underline"
            >
              Refresh
            </button>
          ) : null}
        </div>

        {orderedAnnouncements.length === 0 ? (
          <div className="text-sm text-slate-500">No announcements yet.</div>
        ) : (
          <ul className="space-y-3">
            {orderedAnnouncements.map((a) => {
              const createdBy =
                a.created_by_name || a.created_by_email || a.created_by || "";
              const createdAtLabel = fmtDateTime(a.created_at || a.publish_at);

              return (
                <li
                  key={a.id}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-sm min-w-0"
                >
                  <div className="flex items-start justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-50 truncate">
                        {a.title}
                      </div>

                      <div className="mt-1 text-xs flex flex-wrap gap-2">
                        <span className="px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Level: <b>{a.level}</b>
                          {a.target_id ? ` • ${a.target_name || a.target_id}` : ""}
                        </span>

                        <span className="px-2 py-1 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 border border-orange-100 dark:border-orange-800/40">
                          {prettyAudience(a)}
                        </span>

                        {createdBy ? (
                          <span className="px-2 py-1 rounded-full bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                            By: <b>{createdBy}</b>
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-2 text-slate-700 dark:text-slate-200 whitespace-pre-line break-words">
                        {a.body}
                      </div>

                      <div className="text-xs text-slate-500 mt-2">
                        Published: {createdAtLabel}
                      </div>
                    </div>

                    <button
                      onClick={() => onDeleteAnnouncement?.(a.id)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 shrink-0"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- helpers ------------------------------- */

function toTime(v) {
  if (!v) return null;
  try {
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
